type RHFormSessionSnapshotEnvelope<Snapshot> = {
    version: number
    snapshot: Snapshot
}


/**
 * Keeps unfinished form state for the lifetime of the current browser tab.
 * sessionStorage survives refreshes but is discarded when the tab is closed.
 */
export class RHFormSessionSnapshotStore {

    static readonly _storageKeyPrefix = "RHFormSessionSnapshot:"
    static readonly _storageVersion = 1


    static saveSnapshotForKey<Snapshot>(snapshot: Snapshot, key: string) {
        if (typeof sessionStorage === "undefined" || key.length === 0) {
            return
        }
        const envelope: RHFormSessionSnapshotEnvelope<Snapshot> = {
            version: RHFormSessionSnapshotStore._storageVersion,
            snapshot
        }
        try {
            sessionStorage.setItem(
                RHFormSessionSnapshotStore._storageKey(key),
                JSON.stringify(envelope)
            )
        }
        catch (error) {
            console.warn("Vormi ajutise seisu salvestamine ebaõnnestus.", error)
        }
    }


    static snapshotForKey<Snapshot>(key: string): Snapshot | undefined {
        if (typeof sessionStorage === "undefined" || key.length === 0) {
            return undefined
        }
        const storageKey = RHFormSessionSnapshotStore._storageKey(key)
        try {
            const storedValue = sessionStorage.getItem(storageKey)
            if (!storedValue) {
                return undefined
            }
            const envelope = JSON.parse(storedValue) as RHFormSessionSnapshotEnvelope<Snapshot>
            if (envelope.version !== RHFormSessionSnapshotStore._storageVersion) {
                sessionStorage.removeItem(storageKey)
                return undefined
            }
            return envelope.snapshot
        }
        catch (error) {
            try {
                sessionStorage.removeItem(storageKey)
            }
            catch (_removeError) {
                // Storage may be disabled by the browser. The form must still remain usable.
            }
            console.warn("Vormi ajutise seisu taastamine ebaõnnestus.", error)
            return undefined
        }
    }


    static removeSnapshotForKey(key: string) {
        if (typeof sessionStorage === "undefined" || key.length === 0) {
            return
        }
        try {
            sessionStorage.removeItem(RHFormSessionSnapshotStore._storageKey(key))
        }
        catch (error) {
            console.warn("Vormi ajutise seisu eemaldamine ebaõnnestus.", error)
        }
    }


    static saveSnapshotKeyReferenceForScope(key: string, scope: string) {
        if (typeof sessionStorage === "undefined" || !key || !scope) {
            return
        }
        try {
            sessionStorage.setItem(
                RHFormSessionSnapshotStore._storageKey("reference:" + scope),
                key
            )
        }
        catch (_error) {
            // The form remains usable when browser storage is unavailable.
        }
    }


    static snapshotKeyReferencedByScope(scope: string): string | undefined {
        if (typeof sessionStorage === "undefined" || !scope) {
            return undefined
        }
        try {
            return sessionStorage.getItem(
                RHFormSessionSnapshotStore._storageKey("reference:" + scope)
            ) ?? undefined
        }
        catch (_error) {
            return undefined
        }
    }


    static removeSnapshotKeyReferenceForScope(scope: string) {
        if (typeof sessionStorage === "undefined" || !scope) {
            return
        }
        try {
            sessionStorage.removeItem(
                RHFormSessionSnapshotStore._storageKey("reference:" + scope)
            )
        }
        catch (_error) {
            // The form remains usable when browser storage is unavailable.
        }
    }


    static _storageKey(key: string): string {
        return RHFormSessionSnapshotStore._storageKeyPrefix + key
    }

}
